/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useState } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isWaitlistMode, openWaitlist } from "@/lib/utils/waitlist-mode";
import type { PricingPlan } from "@/content/landing";
import { cn } from "@/lib/utils";

interface PricingCardsProps {
  plans: PricingPlan[];
  isAnnual?: boolean;
  showToggle?: boolean;
}

function PlanCard({
  plan,
  isAnnual,
}: {
  plan: PricingPlan;
  isAnnual: boolean;
}) {
  return (
    <div
      className={cn(
        "card-premium relative flex h-full flex-col p-4 md:p-5",
        plan.comingSoon
          ? "border border-muted-foreground/20 bg-card pt-10 opacity-75"
          : plan.popular
            ? "border-2 border-secondary shadow-xl bg-card pt-10"
            : "border-muted-foreground/20 bg-card",
      )}
    >
      {plan.comingSoon && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 transform">
          <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground whitespace-nowrap shadow-sm">
            Coming Soon
          </span>
        </div>
      )}
      {plan.popular && !plan.comingSoon && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 transform">
          <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground whitespace-nowrap shadow-sm">
            Most Popular
          </span>
        </div>
      )}

      <div className="mb-4 md:mb-5 text-center">
        <h3 className="mb-1.5 text-lg font-bold text-foreground">
          {plan.name}
        </h3>
        <div className="mb-3 flex items-baseline justify-center">
          <span className="text-xl font-bold text-foreground">
            {isAnnual && plan.annualPrice ? plan.annualPrice : plan.price}
          </span>
          <span className="ml-1 text-sm text-muted-foreground">
            {plan.period}
            {isAnnual && plan.name !== "Enterprise" && plan.annualPrice && (
              <span className="block text-xs text-secondary">
                (billed annually)
              </span>
            )}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">{plan.description}</p>
      </div>

      <ul className="mb-4 md:mb-5 flex-grow space-y-2">
        {plan.features.map((feature, featureIndex) => (
          <li key={featureIndex} className="flex items-start text-sm">
            <Check className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-secondary" />
            <span className="text-muted-foreground">{feature}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto">
        <Button
          size="pill"
          disabled={plan.comingSoon}
          onClick={() => {
            if (!plan.comingSoon) {
              if (isWaitlistMode()) {
                openWaitlist();
              } else {
                const params = plan.planId
                  ? `?plan=${encodeURIComponent(plan.planId)}`
                  : "";
                window.location.href = `/auth/sign-up${params}`;
              }
            }
          }}
          className={cn(
            "w-full font-semibold shadow-md transition-all hover:shadow-lg",
            plan.comingSoon
              ? "bg-muted text-muted-foreground cursor-not-allowed"
              : plan.popular
                ? "bg-secondary text-secondary-foreground hover:bg-secondary/90"
                : "bg-foreground text-background hover:bg-foreground/90",
          )}
        >
          {plan.comingSoon
            ? "Coming Soon"
            : isWaitlistMode()
              ? "Join the Waitlist"
              : plan.price === "Custom"
                ? "Contact Sales"
                : "Get Started"}
        </Button>
      </div>
    </div>
  );
}

export function PricingCards({
  plans,
  isAnnual = false,
  showToggle: _showToggle = false,
}: PricingCardsProps) {
  const [activeMobileIndex, setActiveMobileIndex] = useState(() =>
    Math.max(
      0,
      plans.findIndex((p) => p.popular),
    ),
  );

  return (
    <>
      {/* Desktop: side-by-side grid */}
      <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {plans.map((plan, index) => (
          <PlanCard key={index} plan={plan} isAnnual={isAnnual} />
        ))}
      </div>

      {/* Mobile: one plan at a time with tabs */}
      <div className="sm:hidden">
        <div className="flex rounded-xl p-1 bg-muted border border-border mb-5">
          {plans.map((plan, index) => (
            <button
              key={plan.name}
              onClick={() => setActiveMobileIndex(index)}
              className={cn(
                "flex-1 rounded-lg py-2.5 text-sm font-semibold transition-all",
                activeMobileIndex === index
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground",
              )}
            >
              {plan.name}
            </button>
          ))}
        </div>
        {plans[activeMobileIndex] && (
          <PlanCard plan={plans[activeMobileIndex]} isAnnual={isAnnual} />
        )}
      </div>
    </>
  );
}
